import { Box, Checkbox, styled, Theme, Typography } from '@mui/material';
import { palette } from 'view/themes/palette';

export const WizardWrapper = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    width: '560px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: palette.white,
    border: `1px solid ${palette.border.default}`,
    borderRadius: '24px',
    boxShadow: 'none',
    padding: '32px 0',
    outline: 'none',
    overflow: 'hidden',

    // Make FormContainer participate in flex layout
    '& > form': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },

    [theme.breakpoints.down('tablet')]: {
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      padding: '32px 16px',
    },
  };
}) as typeof Box;

export const WizardContent = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    minHeight: 0, // Required for flex children to scroll properly
    overflowY: 'hidden',
    gap: '24px',
    paddingRight: '8px',

    // Scrollbar styling - always visible
    scrollbarWidth: 'thin', // Firefox
    scrollbarColor: '#D8DBE8 #F5F5F5', // Firefox: thumb track

    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#F5F5F5',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D8DBE8',
      borderRadius: '10px',
      border: '2px solid #F5F5F5',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#BFC3D4',
    },
  };
}) as typeof Box;

// Scrollable body for wizard steps.
// Geometry: 32px content gutter on the left, ~16px gap between the scrollbar
// and the wizard edge, ~10px gap between content and the scrollbar. The total
// 16 + 6 (scrollbar) + 10 ≈ 32px matches the left gutter, so content stays
// visually centered. `scrollbar-gutter: stable` reserves the track so layout
// doesn't shift between steps that scroll and steps that don't.
export const ScrollableWizardBody = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    height: '100%',
    marginLeft: '32px',
    marginRight: '16px',
    paddingRight: '10px',
    overflowY: 'auto',
    scrollbarGutter: 'stable',

    scrollbarWidth: 'thin',
    scrollbarColor: '#D8DBE8 transparent',

    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D8DBE8',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#BFC3D4',
    },

    [theme.breakpoints.down('tablet')]: {
      marginLeft: '16px',
      marginRight: '8px',
      paddingRight: '6px',
    },
  };
}) as typeof Box;

export const DotStepperContainer = styled(Box)(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    flexShrink: 0, // Keep stepper visible at top
  };
}) as typeof Box;

export const StepperWithSkip = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: '5px',
    left: '-32px',
  };
}) as typeof Box;

export const Dot = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'active';
  },
})<{ active?: boolean }>(({ active }) => {
  return {
    width: active ? '10px' : '8px',
    height: active ? '10px' : '8px',
    borderRadius: '50%',
    backgroundColor: active ? palette.text.header : '#D8DBE8',
    transition: 'all 0.2s ease',
  };
});

export const StepImage = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '220px',
    '& svg': {
      maxWidth: '280px',
      maxHeight: '220px',
      width: 'auto',
      height: 'auto',
    },
  };
}) as typeof Box;

export const StepHeader = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    textAlign: 'center',
    position: 'relative',
  };
}) as typeof Box;

export const StepTitle = styled(Typography)(() => {
  return {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '29px',
    color: palette.text.header,
  };
}) as typeof Typography;

export const StepSubtitle = styled(Typography)(() => {
  return {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '136%',
    color: palette.text.body,
  };
}) as typeof Typography;

export const StepFormContainer = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };
}) as typeof Box;

export const StepFormContainerTight = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '100%',
    overflowY: 'auto',
  };
}) as typeof Box;

export const StepFormContainerCompact = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  };
}) as typeof Box;

export const InputWithCheckboxWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
  };
}) as typeof Box;

export const CheckboxWithText = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',

    '& > p': {
      color: palette.text.body,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '130%',
    },
  };
}) as typeof Box;

export const CustomCheckbox = styled(Checkbox)(() => {
  return {
    width: '20px',
    height: '20px',
    padding: '0',

    '&.Mui-checked': {
      '.MuiSvgIcon-root': {
        fill: palette.alerts.success,
      },
    },

    '.MuiSvgIcon-root': {
      fill: palette.black,
      fontSize: '20px',
    },
  };
}) as typeof Checkbox;

export const WizardFooter = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0, // Prevent footer from being pushed out of view

    '& button': {
      width: 'auto',
    },

    [theme.breakpoints.down('iphone5')]: {
      flexDirection: 'column',
      '& button': {
        width: '100%',
      },
    },
  };
}) as typeof Box;

export const ChecklistContainer = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '0 24px',
  };
}) as typeof Box;

export const ChecklistItem = styled('div', {
  shouldForwardProp: (prop) => {
    return prop !== 'completed';
  },
})<{ completed?: boolean }>(({ completed }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    backgroundColor: completed ? '#F5F5F5' : 'transparent',
    border: `1px solid ${completed ? '#E0E0E0' : palette.border.default}`,
  };
});

export const ChecklistItemText = styled(Typography)(() => {
  return {
    fontSize: '14px',
    fontWeight: 500,
    color: palette.text.body,
  };
}) as typeof Typography;

export const FormSection = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '8px',
  };
}) as typeof Box;

export const FormSectionLabel = styled(Typography)(() => {
  return {
    fontSize: '14px',
    fontWeight: 600,
    color: palette.text.header,
    marginBottom: '4px',
  };
}) as typeof Typography;

export const LocationTypeContainer = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };
}) as typeof Box;

export const LocationRowContainer = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
  };
}) as typeof Box;

export const LocationAutocompleteWrapper = styled(Box)(() => {
  return {
    flex: 1,
    minWidth: 0,
  };
}) as typeof Box;

export const SkipButton = styled(Typography)(() => {
  return {
    position: 'absolute',
    right: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  };
}) as typeof Typography;

// Work Experience Step styles
export const PositionContainer = styled(Box)(() => {
  return {
    width: '100%',
    marginBottom: '16px',
    padding: '16px',
    border: `1px solid ${palette.border.default}`,
    borderRadius: '12px',
  };
}) as typeof Box;

export const PositionHeader = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  };
}) as typeof Box;

export const PositionTitle = styled(Typography)(() => {
  return {
    color: palette.text.placeholder,
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    textTransform: 'uppercase',
  };
}) as typeof Typography;

export const DeleteButton = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',

    svg: {
      width: '16px',
      height: '16px',
      path: {
        fill: palette.alerts.error,
      },
    },
    span: {
      color: palette.alerts.error,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '18px',
    },
  };
}) as typeof Box;

export const DateContainer = styled(Box)(() => {
  return {
    width: '100%',
    marginBottom: '12px',
  };
}) as typeof Box;

export const DateLabel = styled(Typography)<{ error?: boolean }>(({ error }) => {
  return {
    color: error ? palette.alerts.error : palette.text.header,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '128%',
    marginBottom: '8px',
  };
});

export const DateSelectWrapper = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
  };
}) as typeof Box;

export const AddPositionButton = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px',
  };
}) as typeof Box;
